import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Badge } from '@/components/ui/badge'
import { Info } from 'lucide-react'
import type { HistoricalEpisode, ReplayMode } from '@/types/api'

// =============================================================================
// Schema
// =============================================================================

const historicalReplaySchema = z.object({
  episodeId: z.string().min(1, 'Please select an episode'),
  replayMode: z.enum(['PEAK_TO_TROUGH', 'FULL_PATH']),
})

type HistoricalReplayFormData = z.infer<typeof historicalReplaySchema>

// =============================================================================
// Props
// =============================================================================

interface HistoricalReplayFormProps {
  episodes: HistoricalEpisode[]
  selectedEpisodeId: string
  replayMode: ReplayMode
  onEpisodeChange: (episodeId: string) => void
  onReplayModeChange: (mode: ReplayMode) => void
}

// =============================================================================
// Component
// =============================================================================

export function HistoricalReplayForm({
  episodes,
  selectedEpisodeId,
  replayMode,
  onEpisodeChange,
  onReplayModeChange,
}: HistoricalReplayFormProps) {
  const {
    control,
    formState: { errors },
  } = useForm<HistoricalReplayFormData>({
    resolver: zodResolver(historicalReplaySchema),
    defaultValues: {
      episodeId: selectedEpisodeId,
      replayMode: replayMode,
    },
  })

  const selectedEpisode = episodes.find((e) => e.id === selectedEpisodeId)

  return (
    <TooltipProvider>
      <div className="space-y-6">
        {/* Episode Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Select Historical Episode</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Controller
              name="episodeId"
              control={control}
              render={({ field }) => (
                <div className="space-y-2">
                  <Label htmlFor="episode">Episode</Label>
                  <Select
                    value={field.value}
                    onValueChange={(value) => {
                      field.onChange(value)
                      onEpisodeChange(value)
                    }}
                  >
                    <SelectTrigger id="episode" className="w-full">
                      <SelectValue placeholder="Select a historical episode" />
                    </SelectTrigger>
                    <SelectContent>
                      {episodes.map((episode) => (
                        <SelectItem key={episode.id} value={episode.id}>
                          <div className="flex items-center gap-2">
                            <span>{episode.name}</span>
                            <span className="text-muted-foreground text-xs">
                              ({episode.referencePeriod})
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.episodeId && (
                    <p className="text-sm text-red-500">{errors.episodeId.message}</p>
                  )}
                </div>
              )}
            />

            {/* Episode Description */}
            {selectedEpisode && (
              <div className="bg-muted p-3 rounded-md text-sm">
                <p className="font-medium">{selectedEpisode.name}</p>
                <p className="text-muted-foreground mt-1">{selectedEpisode.description}</p>
                <p className="text-muted-foreground text-xs mt-2">
                  Reference period: {selectedEpisode.referencePeriod}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Replay Mode Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              Replay Mode
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-sm">
                  <p className="text-sm">
                    <strong>Peak-to-Trough:</strong> Single shock analysis comparing peak vs trough values.
                    <br />
                    <strong>Full Path:</strong> Detailed drawdown visualization showing the complete journey.
                  </p>
                </TooltipContent>
              </Tooltip>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Controller
              name="replayMode"
              control={control}
              render={({ field }) => (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => {
                      field.onChange('PEAK_TO_TROUGH')
                      onReplayModeChange('PEAK_TO_TROUGH')
                    }}
                    className={`p-4 rounded-lg border text-left transition-colors ${
                      field.value === 'PEAK_TO_TROUGH'
                        ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                        : 'border-border hover:border-muted-foreground/50'
                    }`}
                  >
                    <div className="font-medium">Peak-to-Trough</div>
                    <div className="text-sm text-muted-foreground mt-1">
                      Single shock analysis
                    </div>
                    <Badge variant="secondary" className="mt-2">
                      Single point comparison
                    </Badge>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      field.onChange('FULL_PATH')
                      onReplayModeChange('FULL_PATH')
                    }}
                    className={`p-4 rounded-lg border text-left transition-colors ${
                      field.value === 'FULL_PATH'
                        ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                        : 'border-border hover:border-muted-foreground/50'
                    }`}
                  >
                    <div className="font-medium">Full Path</div>
                    <div className="text-sm text-muted-foreground mt-1">
                      Detailed drawdown visualization
                    </div>
                    <Badge variant="secondary" className="mt-2">
                      Complete time series
                    </Badge>
                  </button>
                </div>
              )}
            />
          </CardContent>
        </Card>
      </div>
    </TooltipProvider>
  )
}
